import toast from "react-hot-toast";
import classNames from "classnames";
import axiosInstance from "helpers/api";
import { getISODate } from "helpers/utils";
import Button from "components/base/Button";
import ModernTaskList from "./ModernTaskList";
import { useParams } from "react-router-dom";
import { Col, Form, Row } from "react-bootstrap";
import SearchBox from "components/common/SearchBox";
import { Fragment, useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import ConfirmDeleteModal from "../common-components/ConfirmDeleteModal";
import TodoListItem from "components/modules/project-management/todo-list/TodoListItem";

type Inputs = {
  task: string;
  due_date: Date;
  priority: string;
  task_status: string;
};

const TaskPriority = ["Low", "Medium", "High"];
const TaskStatus = ["Pending", "In Progress", "Completed"];


const LeadDetailsTask = ({ tasks, handleSearchTask, onWorkflowProgressRefresh }: any) => {
  const [taskItems, setTaskItems] = useState<any>([]);
  
  let { id } = useParams();

  useEffect(() => {
    setTaskItems(tasks);
  }, [tasks]);

  const handleCreateTask = async (taskData: any) => {
    try {
      const response = await axiosInstance.post(`/leads/${id}/tasks`, {
        task: taskData.task,
        status: taskData.task_status,
        priority: taskData.priority,
        due_date: getISODate(taskData.due_date),
      });
      setTaskItems(response.data.data.tasks);
      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const handleUpdateTask = async (taskId: string, taskData: any) => {
    try {
      const response = await axiosInstance.patch(
        `/leads/${id}/tasks/${taskId}`,
        {
          task: taskData.task,
          status: taskData.task_status,
          priority: taskData.priority,
          due_date: getISODate(taskData.due_date),
        }
      );
      setTaskItems(response.data.data.tasks);
      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await axiosInstance.delete(`/leads/${id}/tasks/${taskId}`);
      setTaskItems((prevTasks: any[]) => prevTasks.filter((task: any) => task.id !== taskId));
      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const handleToggleTask = async (task: any) => {
    try {
      const response = await axiosInstance.patch(
        `/leads/${id}/tasks/${task.id}`,
        {
          is_completed: !task.is_completed,
          status: task.is_completed ? "Pending" : "Completed",
        }
      );
      setTaskItems(response.data.data.tasks);

      // 🚀 WORKFLOW PROGRESSION: If task was completed, check for workflow progression
      if (!task.is_completed) { // Task was just completed
        try {
          console.log(`🔄 Checking workflow progression for completed task: ${task.task}`);
          
          // Call workflow progression check
          const progressionResponse = await fetch(`/api/workflow/tasks/${task.id}/complete`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-User-ID': localStorage.getItem('userId') || '',
              'X-User-JWT': localStorage.getItem('token') || ''
            }
          });
          
          if (progressionResponse.ok) {
            const result = await progressionResponse.json();
            
            // Check if workflow progression was triggered
            if (result.data?.workflowProgression?.progressed) {
              const progression = result.data.workflowProgression;
              console.log(`🚀 Workflow progressed to: ${progression.nextStage?.stageName}`);
              // You could show a toast notification here if desired
              // toast.success(`🚀 Workflow advanced to ${progression.nextStage?.stageName}!`);
            }
            
            // 🔄 REFRESH WORKFLOW PROGRESS: Always refresh the progress panel after task completion
            if (onWorkflowProgressRefresh) {
              onWorkflowProgressRefresh();
            }
          } else {
            console.log(`ℹ️ Task completion noted, but no workflow progression triggered`);
            // Still refresh the progress panel to show updated task counts
            if (onWorkflowProgressRefresh) {
              onWorkflowProgressRefresh();
            }
          }
        } catch (progressionError) {
          console.warn('Could not check workflow progression:', progressionError);
          // Don't fail the main task completion if progression check fails
          // Still refresh the progress panel to show updated task counts
          if (onWorkflowProgressRefresh) {
            onWorkflowProgressRefresh();
          }
        }
      }

      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  return (
    <div className="modern-task-container">
      <ModernTaskList
        tasks={taskItems || []}
        onCreateTask={handleCreateTask}
        onUpdateTask={handleUpdateTask}
        onDeleteTask={handleDeleteTask}
        onToggleTask={handleToggleTask}
        onSearchTask={handleSearchTask}
      />
    </div>
  );
};

export default LeadDetailsTask;
